import React, { useState } from "react";
import {
  Button,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Row,
  Select,
  Space,
} from "antd";
import { EditOutlined } from "@ant-design/icons";
import { FormInstance } from "antd/es/form";
import styled from "@emotion/styled";
import { Employer } from "../employer-list/list";
import { AdminSelect } from "components/admin-select";
import { District } from "components/district";
import { employerStatus, gender } from "utils/common";
import { changeArrayToStringInObject } from "utils";

interface SearchPanelProps extends Employer {
  onUpdate: (value: Partial<Employer>) => void;
  loading: boolean;
  reload: () => void;
}

export const IndividualEmployerDetails = ({
  onUpdate,
  reload,
  loading,
  ...data
}: SearchPanelProps) => {
  const formatData = {
    uuid: data.uuid,
    contactPerson: data.contactPerson,
    contactNumber: data.contactNumber,
    district: data?.district?.split(",") || [],
    address: data.address,
    email: data.email,
    status: data.status,
    personInCharge: data.personInCharge?.id,
  };

  const editBasicInfoFormRef = React.createRef<FormInstance>();
  const [editBasicInfoForm] = Form.useForm();
  const [showEditBasicInfoModal, setShowEditBasicInfoModal] = useState(false);

  const editContactFormRef = React.createRef<FormInstance>();
  const [editContactForm] = Form.useForm();
  const [showEditContactModal, setShowEditContactModal] = useState(false);

  const editAdminInfoFormRef = React.createRef<FormInstance>();
  const [editAdminInfoForm] = Form.useForm();
  const [showEditAdminInfoModal, setShowEditAdminInfoModal] = useState(false);

  const editPatientFormRef = React.createRef<FormInstance>();
  const [editPatientForm] = Form.useForm();
  const [showEditPatientModal, setShowEditPatientModal] = useState(false);

  const onEditFormClick = (type: string) => {
    switch (type) {
      case "basic":
        setShowEditBasicInfoModal(!showEditBasicInfoModal);
        break;
      case "contact":
        editContactForm.setFieldsValue(formatData);
        setShowEditContactModal(!showEditContactModal);
        break;
      case "adminInfo":
        setShowEditAdminInfoModal(!showEditAdminInfoModal);
        break;
      case "patient":
        setShowEditPatientModal(!showEditPatientModal);
        break;
    }
  };

  const onEditModalSubmit = async (values: Partial<Employer>) => {
    await onUpdate(changeArrayToStringInObject(values));
    reload();

    if (showEditBasicInfoModal) {
      setShowEditBasicInfoModal(!showEditBasicInfoModal);
    }

    if (showEditContactModal) {
      setShowEditContactModal(!showEditContactModal);
    }

    if (showEditAdminInfoModal) {
      setShowEditAdminInfoModal(!showEditAdminInfoModal);
    }

    if (showEditPatientModal) {
      setShowEditPatientModal(!showEditPatientModal);
    }
  };

  return (
    <>
      <SectionContainer>
        <SectionTitle>employer information</SectionTitle>
        <EditButton>
          <EditOutlined onClick={() => onEditFormClick("basic")} />
        </EditButton>
        <DataContainer>
          <DataTitle>employer:</DataTitle>
          <DataContent>{data?.contactPerson}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>relationship with patients:</DataTitle>
          <DataContent>{data?.relation}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>community voucher number:</DataTitle>
          <DataContent>{data?.code}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>community voucher percentage:</DataTitle>
          <DataContent>{data?.codePercentage && `${data?.codePercentage}%`}</DataContent>
        </DataContainer>
      </SectionContainer>

      <SectionContainer>
        <SectionTitle>contact information</SectionTitle>
        <EditButton>
          <EditOutlined onClick={() => onEditFormClick("contact")} />
        </EditButton>
        <DataContainer>
          <DataTitle>contact number:</DataTitle>
          <DataContent>{data?.contactNumber}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>email:</DataTitle>
          <DataContent>{data?.email}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>regions served:</DataTitle>
          <DataContent>{data?.district}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>full service address:</DataTitle>
          <DataContent>{data?.address}</DataContent>
        </DataContainer>
      </SectionContainer>

      <SectionContainer>
        <SectionTitle>UniCare remark</SectionTitle>
        <EditButton>
          <EditOutlined onClick={() => onEditFormClick("adminInfo")} />
        </EditButton>
        <DataContainer>
          <DataTitle>person in charge:</DataTitle>
          <DataContent>{data?.personInCharge?.username}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>status:</DataTitle>
          <DataContent>{employerStatus(data?.status)}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>remark:</DataTitle>
          <DataContent>{data?.remark}</DataContent>
        </DataContainer>
      </SectionContainer>

      <SectionContainer>
        <SectionTitle>patient information</SectionTitle>
        <EditButton>
          <EditOutlined onClick={() => onEditFormClick("patient")} />
        </EditButton>
        <DataContainer>
          <DataTitle>patient's name:</DataTitle>
          <DataContent>{data?.patientName}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>id card number/passport number:</DataTitle>
          <DataContent>{data?.patientIdCardNum}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>gender:</DataTitle>
          <DataContent>{gender(data?.patientGender)}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>age:</DataTitle>
          <DataContent>{data?.patientAge}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>height(cm):</DataTitle>
          <DataContent>{data?.patientHeight}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>weight(kg):</DataTitle>
          <DataContent>{data?.patientWeight}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>disease:</DataTitle>
          <DataContent>{data?.patientDisease}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>cancer:</DataTitle>
          <DataContent>{data?.cancer}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>other diseases:</DataTitle>
          <DataContent>{data?.otherDisease}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>throat tube:</DataTitle>
          <DataContent>{data?.throatPipe}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>other tube:</DataTitle>
          <DataContent>{data?.otherPipe}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>physical condition:</DataTitle>
          <DataContent>{data?.bodyCondition}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>psychiatric condition:</DataTitle>
          <DataContent>{data?.mentalCondition}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>ability to perform activities:</DataTitle>
          <DataContent>{data?.ability}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>incontinence:</DataTitle>
          <DataContent>{data?.incontinence}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>primary care:</DataTitle>
          <DataContent>{data?.mainCare}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>needles:</DataTitle>
          <DataContent>{data?.injection}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>oral medications:</DataTitle>
          <DataContent>{data?.oralDrugs}</DataContent>
        </DataContainer>
        <DataContainer>
          <DataTitle>other:</DataTitle>
          <DataContent>{data?.otherNursing}</DataContent>
        </DataContainer>
      </SectionContainer>

      <Modal
        title={"edit employer information"}
        visible={showEditBasicInfoModal}
        footer={null}
        onCancel={() => onEditFormClick("basic")}
      >
        <Form
          form={editBasicInfoForm}
          ref={editBasicInfoFormRef}
          layout="vertical"
          initialValues={data}
          onFinish={onEditModalSubmit}
        >
          <Form.Item name="uuid" label="employerId" hidden>
            <Input placeholder="employerId" />
          </Form.Item>

          <Form.Item
            name="contactPerson"
            label="contact name"
            rules={[{ required: true, message: "please enter a contact name" }]}
          >
            <Input placeholder="contact name" />
          </Form.Item>

          <Form.Item
            name="relation"
            label="relationship with patients"
            rules={[{ required: true, message: "please enter the relationship with the patient" }]}
          >
            <Input placeholder="relationship with patients" />
          </Form.Item>

          <Form.Item name="code" label="community voucher number">
            <Input placeholder="community voucher number" />
          </Form.Item>

          <Form.Item name="codePercentage" label="community voucher percentage">
            <InputNumber placeholder="community voucher percentage" style={{ width: "100%" }} />
          </Form.Item>

          <Button
            htmlType="button"
            onClick={() => onEditFormClick("basic")}
            style={{ marginRight: 20 }}
          >
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Submit
          </Button>
        </Form>
      </Modal>

      <Modal
        title={"edit your contact information"}
        visible={showEditContactModal}
        footer={null}
        onCancel={() => onEditFormClick("contact")}
      >
        <Form
          form={editContactForm}
          ref={editContactFormRef}
          layout="vertical"
          initialValues={data}
          onFinish={onEditModalSubmit}
        >
          <Form.Item name="uuid" label="employerId" hidden>
            <Input placeholder="employerId" />
          </Form.Item>

          <Form.Item
            name="contactNumber"
            label="contact number"
            rules={[{ required: true, message: "please enter your contact number" }]}
          >
            <Input placeholder="contact number" />
          </Form.Item>

          <Form.Item
            name="email"
            label="email"
            rules={[{ required: true, message: "please enter your email address" }]}
          >
            <Input placeholder="email" />
          </Form.Item>

          <Form.Item
            name="district"
            label="regions served"
            rules={[{ required: true, message: "please enter the service area" }]}
          >
            <District />
          </Form.Item>

          <Form.Item
            name="address"
            label="full service address"
            rules={[{ required: true, message: "please enter the full service address" }]}
          >
            <Input placeholder="full service address" />
          </Form.Item>

          <Button
            htmlType="button"
            onClick={() => onEditFormClick("contact")}
            style={{ marginRight: 20 }}
          >
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Submit
          </Button>
        </Form>
      </Modal>

      <Modal
        title={"edit UniCare remark"}
        visible={showEditAdminInfoModal}
        footer={null}
        onCancel={() => onEditFormClick("adminInfo")}
      >
        <Form
          form={editAdminInfoForm}
          ref={editAdminInfoFormRef}
          layout="vertical"
          initialValues={data}
          onFinish={onEditModalSubmit}
        >
          <Form.Item name="uuid" label="employerId" hidden>
            <Input placeholder="employerId" />
          </Form.Item>

          <Form.Item name="personInCharge" label="responsible for employees">
            <AdminSelect placeholder="responsible for employees" />
          </Form.Item>

          <Form.Item name="status" label="status">
            <Select placeholder="status">
              <Select.Option value={0}>enable</Select.Option>
              <Select.Option value={1}>troublemakers</Select.Option>
              <Select.Option value={2}>deactivated</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item name="remark" label="remark">
            <Input.TextArea
              placeholder="remark"
              autoSize={{ minRows: 2, maxRows: 6 }}
            />
          </Form.Item>

          <Button
            htmlType="button"
            onClick={() => onEditFormClick("adminInfo")}
            style={{ marginRight: 20 }}
          >
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Submit
          </Button>
        </Form>
      </Modal>

      <Modal
        title={"edit patient information"}
        visible={showEditPatientModal}
        footer={null}
        onCancel={() => onEditFormClick("patient")}
        width={1100}
      >
        <Form
          form={editPatientForm}
          ref={editPatientFormRef}
          layout="vertical"
          initialValues={data}
          onFinish={onEditModalSubmit}
        >
          <Form.Item name="uuid" label="employerId" hidden>
            <Input placeholder="employerId" />
          </Form.Item>

          <Form.Item
            name="patientName"
            label="patient's name"
            rules={[{ required: true, message: "please enter the patient's name" }]}
          >
            <Input placeholder="patient's name" />
          </Form.Item>

          <Form.Item
            name="patientIdCardNum"
            label="id card/passport number"
            rules={[
              { required: true, message: "please enter your ID card/passport number" },
            ]}
          >
            <Input placeholder="id card number/passport number" />
          </Form.Item>

          <Form.Item
            name="patientGender"
            label="gender"
            rules={[{ required: true, message: "please enter your gender" }]}
          >
            <Radio.Group>
              <Radio value={1}>females</Radio>
              <Radio value={0}>male</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="patientAge"
            label="age"
            rules={[{ required: true, message: "please enter your age" }]}
          >
            <InputNumber min={0} max={150} placeholder="age" />
          </Form.Item>

          <Form.Item
            name="patientHeight"
            label="height(cm)"
            rules={[{ required: true, message: "please enter your height" }]}
          >
            <InputNumber min={0} max={300} placeholder="height(cm)" />
          </Form.Item>

          <Form.Item
            name="patientWeight"
            label="weight(kg)"
            rules={[{ required: true, message: "please enter your weight" }]}
          >
            <InputNumber min={0} max={1000} placeholder="weight(kg)" />
          </Form.Item>

          <Form.Item name="patientDisease" label="disease">
            <Checkbox.Group>
              <Row>
                <Col span={6}>
                  <Checkbox value="stroke">stroke</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="diabetes">diabetes</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="hypertension">hypertension</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="heart disease">heart disease</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="hepatitis">肝炎</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="contagious disease">contagious disease</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="kidney failure">kidney failure</Checkbox>
                </Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item name="cancer" label="cancer">
            <Input placeholder="please specify the organ/position" />
          </Form.Item>
          <Form.Item name="otherDisease" label="other diseases">
            <Input placeholder="please specify" />
          </Form.Item>
          <Form.Item name="throatPipe" label="throat pipe">
            <Checkbox.Group>
              <Row>
                <Col span={6}>
                  <Checkbox value="stomach tube">stomach tube</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="urinate tube">urinate tube</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="PEG">PEG</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="pneumaticostomy">pneumaticostomy</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="oxygen gas tube">oxygen gas tube</Checkbox>
                </Col>
                <Col span={6}>
                  <Checkbox value="wash the belly tube">wash the belly tube</Checkbox>
                </Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item name="otherPipe" label="other tube">
            <Input placeholder="please specify" />
          </Form.Item>
          <Form.Item
            name="bodyCondition"
            label="physical condition"
            rules={[{ required: true, message: "please enter your medical condition" }]}
          >
            <Radio.Group>
              <Radio value="stable and healthy">stable and healthy</Radio>
              <Radio value="the condition is stable">the condition is stable</Radio>
              <Radio value="the situation is unstable">the situation is unstable</Radio>
              <Radio value="the situation is critical">the situation is critical</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="mentalCondition"
            label="psychiatric condition"
            rules={[{ required: true, message: "please enter your mental condition" }]}
          >
            <Radio.Group>
              <Radio value="sober">sober</Radio>
              <Radio value="drowsiness/sluggish">drowsiness/sluggish</Radio>
              <Radio value="confusion/the answer is confused">confusion/the answer is confused</Radio>
              <Radio value="coma/can't respond">coma/can't respond</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="ability"
            label="ability to perform activities"
            rules={[{ required: true, message: "please enter mobility" }]}
          >
            <Radio.Group>
              <Space direction="vertical">
                <Radio value="get out of bed and move around often">get out of bed and move around often</Radio>
                <Radio value="need assistance to walk">need assistance to walk</Radio>
                <Radio value="seated position only(wheelchair)">seated position only(wheelchair)</Radio>
                <Radio value="can't move(inability to move limbs or bodies, poses cannot be changed)">
                  "can't move(inability to move limbs or bodies, poses cannot be changed)"
                </Radio>
                <Radio value="very restrictive(assistance is required to change positions, mobility of limbs or bodies is carried out with assistance)">
                  very restrictive(assistance is required to change positions, mobility of limbs or bodies is carried out with assistance)
                </Radio>
                <Radio value=" a little restricted(able to control and move limbs or bodies, but there are limitations, assistance is required to change positions)">
                  a little restricted(able to control and move limbs or bodies, but there are limitations, assistance is required to change positions)
                </Radio>
                <Radio value="there is no limit(often can move limbs or bodies independently)">
                  there is no limit(often can move limbs or bodies independently)
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="incontinence"
            label="incontinent"
            rules={[{ required: true, message: "please enter incontinence" }]}
          >
            <Radio.Group>
              <Space direction="vertical">
                <Radio value="nope">nope</Radio>
                <Radio value="incontinence(inability to control bowel and bowel movements，about 7 / 10 times a day)">
                  incontinence(inability to control bowel and bowel movements，about 7 / 10 times a day)
                </Radio>
                <Radio value=" usually urinary incontinence(pee about 3-6 urinary incontinence/days，or in the past 24-hour diarrhea)">
                  usually urinary incontinence(pee about 3-6 urinary incontinence/days，or in the past 24-hour diarrhea)
                </Radio>
                <Radio value=" sometime(about 1-2 fecal or urinary incontinence/day, but not because of diuretics/cathartic，have a urine tube but can restrain stool)">
                  sometime(about 1-2 fecal or urinary incontinence/day, but not because of diuretics/cathartic，have a urine tube but can restrain stool)
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          <Form.Item name="mainCare" label="primary care">
            <Checkbox.Group>
              <Row>
                <Col span={5}>
                  <Checkbox value="wash your belly">wash your belly</Checkbox>
                </Col>
                <Col span={5}>
                  <Checkbox value="blood washing">blood washing</Checkbox>
                </Col>
                <Col span={5}>
                  <Checkbox value="shower/wipe your body">shower/wipe your body</Checkbox>
                </Col>
                <Col span={5}>
                  <Checkbox value="turn">turn</Checkbox>
                </Col>
                <Col span={4}>
                  <Checkbox value="rice bowl">rice bowl</Checkbox>
                </Col>
                <Col span={5}>
                  <Checkbox value="phlegm">phlegm</Checkbox>
                </Col>
                <Col span={5}>
                  <Checkbox value="wash the wound">wash the wound</Checkbox>
                </Col>
                <Col span={5}>
                  <Checkbox value="diaper changes">diaper changes</Checkbox>
                </Col>
                <Col span={5}>
                  <Checkbox value="simple movement">simple movement</Checkbox>
                </Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>

          <Form.Item name="injection" label="needles">
            <Input placeholder="indicate the name of the drug" />
          </Form.Item>

          <Form.Item name="oralDrugs" label="oral medications">
            <Input placeholder="indicate the name of the drug" />
          </Form.Item>
          <Form.Item name="otherNursing" label="other cares">
            <Input placeholder="please specify" />
          </Form.Item>

          <Button
            htmlType="button"
            onClick={() => onEditFormClick("patient")}
            style={{ marginRight: 20 }}
          >
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            Submit
          </Button>
        </Form>
      </Modal>
    </>
  );
};

const SectionContainer = styled.div`
  background-color: #ffffff;
  width: 100%;
  padding: 15px;
  margin-bottom: 10px;
  position: relative;
`;

const SectionTitle = styled.div`
  color: #3c3e42;
  font-size: 20px;
  font-weight: bold;
  padding-bottom: 6px;
`;

const EditButton = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
`;

const DataContainer = styled.div`
  // padding-top: 2px;
`;

const DataTitle = styled.div`
  display: inline-block;
  font-size: 14px;
  color: #8d939e;
  padding-right: 5px;
`;

const DataContent = styled.div`
  display: inline-block;
  font-size: 14px;
  color: #3c3e42;
  font-weight: 500;
`;
